import { Component, Input, TemplateRef, Type, ViewChild, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { noop } from 'lodash';
import { ButtonComponent, MbsSize, ModalService, ModalSettings } from 'mbs-ui-kit';
import { TFARequireModalComponent } from 'mbs-ui-shared@components';

type ResultPoint = { path: string; restorePoint: string };

@Component({
  selector: 'app-password-recovery-handler',
  templateUrl: './password-recovery-handler.component.html',
  standalone: true,
  imports: [ButtonComponent, I18NextPipe]
})
export class PasswordRecoveryHandlerComponent {
  private modalService = inject(ModalService);
  i18nPipe = inject(I18NextPipe);
  private ability = inject(AbilityService);

  readonly FIRST_BACKUP_AGENT_SUPPORTED_PASSWORD_RECOVERY = 770;

  @Input() resultPoint: ResultPoint;
  @Input() hid: string;
  @Input() passwordControl: AbstractControl;
  @Input() backupVersionUpdated: string;

  @ViewChild('versionUnsupportedModal', { static: true, read: TemplateRef }) versionUnsupportedModal: TemplateRef<any>;

  handleRestorePassword(): void {
    if (this.ability.cannot('read', '2FAEnabled')) {
      this.modalService
        .openCustom(TFARequireModalComponent, {
          size: MbsSize.sm,
          data: {
            actionName: 'Password Recovery'
          }
        })
        .finally(noop);
      return;
    }
    if (Number(this.backupVersionUpdated.slice(0, 3)) < this.FIRST_BACKUP_AGENT_SUPPORTED_PASSWORD_RECOVERY) {
      const modalSettings: ModalSettings = {
        header: { title: this.i18nPipe.transform('shared:pass-recovery.unsupported_version', { format: 'title' }) },
        footer: { okButton: { show: false }, cancelButton: { text: this.i18nPipe.transform('shared:buttons.close', { format: 'title' }) } }
      };
      this.modalService.open(modalSettings, this.versionUnsupportedModal).finally(noop);

      return;
    }

    import('mbs-ui-shared@modules/password-recovery/modals/password-recovery-modal/password-recovery-modal.component').then((module) => {
      const PasswordRecoveryModalComponent = module.PasswordRecoveryModalComponent as Type<any>;
      this.modalService
        .openRef(PasswordRecoveryModalComponent, {
          data: {
            path: this.getResultPointPath(this.resultPoint),
            hid: this.hid
          }
        })
        .result.then((pass) => {
          this.passwordControl.setValue(pass);
        })
        .catch(noop);
    });
  }

  private getResultPointPath(resultPoint: ResultPoint): string {
    if (!resultPoint?.path) return '';

    if (!resultPoint.restorePoint) return resultPoint?.path;

    const splitted = resultPoint.path.split('\\');
    const sliceIndex = splitted.indexOf(resultPoint.restorePoint);

    return splitted.slice(0, sliceIndex - splitted.length).join('\\');
  }
}
